import { Service } from '../entities/service';
import { portalSlug } from './portal-slug';

/**
 * Client-side mirror of fusion-server's seedTakenPortalCodes +
 * assignFormatPortalCode (util/portal-admin.util.js), used when the admin
 * publishes several datasets in one go from the dashboard save prompt and
 * never types a code.
 *
 * The server stays authoritative: validatePortalCodes re-checks every code on
 * save and refuses the whole service with a 409. Minting here only keeps a
 * batch of auto-generated codes from colliding with each other or with an
 * already-claimed one, which is exactly the case the server would reject and
 * the admin could not fix without visiting each service by hand.
 */

/**
 * The bits of a file format this module reads. `Service.fileFormats` is typed
 * `object[]`, so callers index it — this names what they must supply.
 */
export interface MintableFormat {
    id?: string | number;
    name?: string;
    portalCode?: string;
}

/** Every code already claimed: service codes and existing portalCodes, lower-cased. */
export function takenPortalCodes(services: Service[]): Set<string> {
    const taken = new Set<string>();
    (services || []).forEach(svc => {
        if (svc?.code) taken.add(String(svc.code).toLowerCase());
        (svc?.fileFormats || []).forEach(ff => {
            const code = (ff as MintableFormat)?.portalCode;
            if (code) taken.add(String(code).toLowerCase());
        });
    });
    return taken;
}

/** First of base, base-2, base-3 ... not already in `taken`. Case-insensitive, like the server's conflict check. */
function uniqueCode(base: string, taken: Set<string>): string {
    if (!taken.has(base.toLowerCase())) return base;
    let n = 2;
    while (taken.has((base + '-' + n).toLowerCase())) n++;
    return base + '-' + n;
}

/**
 * The code to store for `fileFormat`: its own, if it has one; else a free slug
 * of its name, falling back to the service code and finally to a per-format
 * `dataset-<id>` so a format named only in punctuation still gets a code
 * distinct from its siblings'.
 *
 * Does not mutate `taken` — the caller owns that set across a whole batch and
 * must add each returned code to it before minting the next.
 */
export function mintPortalCode(fileFormat: MintableFormat, serviceCode: string, taken: Set<string>): string {
    const ff = fileFormat || {};
    if (ff.portalCode) return ff.portalCode;
    const base = portalSlug(ff.name) || portalSlug(serviceCode) || 'dataset-' + String(ff.id ?? '');
    return uniqueCode(base, taken);
}
